import React, { useEffect, useState } from 'react'

// Kendo functionality imports
import {
    Grid,
    GridColumn,
    GridColumnMenuCheckboxFilter,
    GridColumnMenuFilter,
    GridColumnMenuFilterUIProps,
    GridDataStateChangeEvent,
} from '@progress/kendo-react-grid'

import { DataResult, process, State } from '@progress/kendo-data-query'

import { formatDate } from '@telerik/kendo-intl'
import { PointOfSaleReport } from '../../features/invoice/invoiceAPI'
import useWindowDimensions from '../../GlobalComponents/GlobalFunctions'

export const KendoGridShortDateCell = (props: any) => {
    const value = props.dataItem[props.field]
    return <td>{value && formatDate(new Date(value), 'd')}</td>
}

export const BooleanCell = (props: any) => {
    const value = props.dataItem[props.field]
    let textColor = 'red'
    if (value) {
        textColor = 'green'
    }
    return <td style={{ color: textColor }}>{value.toString()}</td>
}

export const GridData: React.FC<{
    data: PointOfSaleReport[] | undefined
    loading: boolean
    error: any
    editRowClick: (event: any) => void
}> = (props) => {
    // Window screen dimensions
    const { height } = useWindowDimensions()

    // Filtering
    const createDataState = (dataState: State) => {
        let data: PointOfSaleReport[] = []
        if (props.data) {
            data = props.data
        }
        return {
            result: process(data.slice(0), dataState),
            dataState: dataState,
        }
    }

    let initialState = createDataState({
        skip: 0,
    })

    const [result, setResult] = useState<DataResult>(initialState.result)
    const [dataState, setDataState] = useState<State>(initialState.dataState)

    const dataStateChange = (event: GridDataStateChangeEvent) => {
        let updatedState = createDataState(event.dataState)
        setResult(updatedState.result)
        setDataState(updatedState.dataState)
    }

    const DescriptionFilterCell = (props: any) => {
        return <GridColumnMenuFilter {...props} filterUI={DescriptionFilterUI} expanded={true} />
    }

    const DescriptionFilterUI = (props: GridColumnMenuFilterUIProps) => {
        const onChange = (event: any) => {
            const value = event.target.value
            const { firstFilterProps } = props

            firstFilterProps.onChange({
                value,
                operator: 'contains',
                syntheticEvent: event.syntheticEvent,
            })
        }

        return (
            <div style={{ margin: 25, display: 'flex', justifyContent: 'center' }}>
                <input
                    defaultValue={''}
                    onChange={onChange}
                    style={{ width: 170, fontSize: 16, padding: 5, fontWeight: 300 }}
                />
            </div>
        )
    }

    const [fullReportNameList, setFullReportNameList] = useState<string[]>([])

    const ReportNameCheckboxFilter = (props: any) => {
        return (
            <div>
                <GridColumnMenuCheckboxFilter
                    {...props}
                    data={
                        fullReportNameList &&
                        fullReportNameList.map((str: string) => {
                            return { reportName: str }
                        })
                    }
                    expanded={true}
                />
            </div>
        )
    }

    useEffect(() => {
        if (!props.data) return
        setFullReportNameList(props.data.map((c) => c.reportName!))
        setFullFrequencyList(props.data.map((c) => c.frequency!))
        setFullActiveList(props.data.map((c) => c.active!))
        setFullSuccessList(props.data.map((c) => c.success!))
        setFullRunByList(props.data.map((c) => c.runBy!))
        setResult(process(props.data.slice(0), dataState))
    }, [props.data])

    const [fullFrequencyList, setFullFrequencyList] = useState<string[]>([])

    const FrequencyCheckboxFilter = (props: any) => {
        return (
            <div>
                <GridColumnMenuCheckboxFilter
                    {...props}
                    data={
                        fullFrequencyList &&
                        fullFrequencyList.map((str: string) => {
                            return { frequency: str }
                        })
                    }
                    expanded={true}
                />
            </div>
        )
    }

    const [fullActiveList, setFullActiveList] = useState<boolean[]>([])

    const ActiveCheckboxFilter = (props: any) => {
        return (
            <div>
                <GridColumnMenuCheckboxFilter
                    {...props}
                    data={
                        fullActiveList &&
                        fullActiveList.map((bool: boolean) => {
                            return { active: bool }
                        })
                    }
                    expanded={true}
                />
            </div>
        )
    }

    const [fullSuccessList, setFullSuccessList] = useState<boolean[]>([])

    const SuccessCheckboxFilter = (props: any) => {
        return (
            <div>
                <GridColumnMenuCheckboxFilter
                    {...props}
                    data={
                        fullSuccessList &&
                        fullSuccessList.map((bool: boolean) => {
                            return { success: bool }
                        })
                    }
                    expanded={true}
                />
            </div>
        )
    }

    const [fullRunByList, setFullRunByList] = useState<string[]>([])

    const RunByCheckboxFilter = (props: any) => {
        return (
            <div>
                <GridColumnMenuCheckboxFilter
                    {...props}
                    data={
                        fullRunByList &&
                        fullRunByList.map((str: string) => {
                            return { runBy: str }
                        })
                    }
                    expanded={true}
                />
            </div>
        )
    }

    return (
        <div>
            {props.data && (
                <Grid
                    data={result}
                    {...dataState}
                    onDataStateChange={dataStateChange}
                    onRowClick={props.editRowClick}
                    scrollable="scrollable"
                    sortable={true}
                    style={{ height: height - 105 }}
                >
                    <GridColumn
                        field="reportName"
                        title="Report Name"
                        width="200px"
                        columnMenu={ReportNameCheckboxFilter}
                    />
                    <GridColumn
                        field="description"
                        title="Internal Description"
                        columnMenu={DescriptionFilterCell}
                        width="275px"
                    />
                    <GridColumn
                        field="frequency"
                        title="Frequency"
                        width="205px"
                        columnMenu={FrequencyCheckboxFilter}
                    />
                    <GridColumn
                        field="active"
                        title="Active"
                        width="150px"
                        filter="boolean"
                        cell={BooleanCell}
                        columnMenu={ActiveCheckboxFilter}
                    />
                    <GridColumn
                        field="lastRun"
                        title="Last Run"
                        cell={KendoGridShortDateCell}
                        filter="date"
                        width="200px"
                    />
                    <GridColumn
                        field="dateRange"
                        title="Date Range"
                        width="200px"
                        filterable={false}
                    />
                    <GridColumn
                        field="success"
                        title="Success"
                        width="150px"
                        filter="boolean"
                        cell={BooleanCell}
                        columnMenu={SuccessCheckboxFilter}
                    />
                    <GridColumn
                        field="runBy"
                        title="Run By"
                        width="137px"
                        columnMenu={RunByCheckboxFilter}
                    />
                </Grid>
            )}
        </div>
    )
}
